import type { ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

type Mode = "light" | "dark";
type Accent = "blue" | "orange" | "violet" | "green";

interface ThemeCtx {
	mode: Mode;
	accent: Accent;
	setMode: (m: Mode) => void;
	setAccent: (a: Accent) => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [mode, setModeState] = useState<Mode>(
		() => (localStorage.getItem("il:theme") as Mode) || "light",
	);
	const [accent, setAccentState] = useState<Accent>(
		() => (localStorage.getItem("il:accent") as Accent) || "blue",
	);

	useEffect(() => {
		const root = document.documentElement;
		root.setAttribute("data-theme", mode);
		root.setAttribute("data-accent", accent);
	}, [mode, accent]);

	const setMode = useCallback((m: Mode) => {
		setModeState(m);
		localStorage.setItem("il:theme", m);
	}, []);

	const setAccent = useCallback((a: Accent) => {
		setAccentState(a);
		localStorage.setItem("il:accent", a);
	}, []);

	const value = useMemo(
		() => ({ mode, accent, setMode, setAccent }),
		[mode, accent, setMode, setAccent],
	);
	return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
	const ctx = useContext(Ctx);
	if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
	return ctx;
}
